import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Shield, 
  Plus, 
  Trash2, 
  Loader2, 
  Globe, 
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';

const AdminSecuritySettings = () => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // IP Whitelist State
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);
  const [allowLocalhost, setAllowLocalhost] = useState(true);
  const [ipAddresses, setIpAddresses] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [newIpDescription, setNewIpDescription] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await apiCall('/api/admin/security/ip-whitelist');
      setWhitelistEnabled(data.enabled || false);
      setAllowLocalhost(data.allow_localhost !== false);
      setIpAddresses(data.ip_addresses || []);
    } catch (error) {
      console.error('Failed to load security settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load security settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ── iter-231B.UI — Compliance Enforcement Settings ──────────────
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [complianceSaving, setComplianceSaving] = useState(false);
  const [autoCancelTier2, setAutoCancelTier2] = useState(true);
  const [complianceUpdatedAt, setComplianceUpdatedAt] = useState(null);
  const [complianceUpdatedBy, setComplianceUpdatedBy] = useState(null);
  const [violationCounts, setViolationCounts] = useState({total: 0, auto_cancelled: 0, logged_only: 0, cancel_failed: 0});

  const loadComplianceSettings = async () => {
    try {
      const data = await apiCall('/api/admin/compliance/enforcement/settings');
      setAutoCancelTier2(data.auto_cancel_tier2_violations !== false);
      setComplianceUpdatedAt(data.updated_at || null);
      setComplianceUpdatedBy(data.updated_by || null);
      setViolationCounts(data.violation_counts || {total: 0, auto_cancelled: 0, logged_only: 0, cancel_failed: 0});
    } catch (e) {
      console.error('Failed to load compliance settings:', e);
    } finally {
      setComplianceLoading(false);
    }
  };

  useEffect(() => {
    loadComplianceSettings();
  }, []);

  const saveComplianceSettings = async (nextValue) => {
    setComplianceSaving(true);
    try {
      const data = await apiCall('/api/admin/compliance/enforcement/settings', {
        method: 'POST',
        body: JSON.stringify({ auto_cancel_tier2_violations: !!nextValue }),
      });
      setAutoCancelTier2(!!data.auto_cancel_tier2_violations);
      toast({
        title: nextValue ? 'Auto-cancel enabled' : 'Switched to log-only mode',
        description: nextValue
          ? 'Tier-2 GST out-of-state orders will be auto-cancelled with refund.'
          : 'Violations will be logged only — no order will be auto-cancelled.',
      });
      // Re-fetch counts after toggle.
      loadComplianceSettings();
    } catch (e) {
      toast({
        title: 'Toggle failed',
        description: e?.message || 'Could not update compliance settings.',
        variant: 'destructive',
      });
    } finally {
      setComplianceSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiCall('/api/admin/security/ip-whitelist', {
        method: 'POST',
        body: JSON.stringify({
          enabled: whitelistEnabled,
          allow_localhost: allowLocalhost,
          ip_addresses: ipAddresses
        })
      });
      
      toast({
        title: 'Success',
        description: 'Security settings saved successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const addIpAddress = () => {
    if (!newIp.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an IP address',
        variant: 'destructive'
      });
      return;
    }

    // Basic IP validation (IPv4)
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    
    if (!ipv4Regex.test(newIp) && !cidrRegex.test(newIp) && newIp !== '*') {
      toast({
        title: 'Invalid IP',
        description: 'Please enter a valid IPv4 address (e.g., 192.168.1.1) or CIDR range (e.g., 192.168.1.0/24)',
        variant: 'destructive'
      });
      return;
    }

    // Check for duplicates
    if (ipAddresses.some(ip => ip.ip_address === newIp)) {
      toast({
        title: 'Duplicate',
        description: 'This IP address is already in the whitelist',
        variant: 'destructive'
      });
      return;
    }

    setIpAddresses([
      ...ipAddresses,
      {
        ip_address: newIp,
        description: newIpDescription || 'No description',
        added_by: user?.email,
        added_at: new Date().toISOString()
      }
    ]);
    
    setNewIp('');
    setNewIpDescription('');
    
    toast({
      title: 'IP Added',
      description: `${newIp} added to whitelist. Don't forget to save!`
    });
  };

  const removeIpAddress = (ipToRemove) => {
    setIpAddresses(ipAddresses.filter(ip => ip.ip_address !== ipToRemove));
    toast({
      title: 'IP Removed',
      description: `${ipToRemove} removed. Don't forget to save!`
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-7 w-7 text-slate-700" />
              Security Settings
            </h1>
            <p className="text-gray-500 mt-1">
              Configure admin portal security options
            </p>
          </div>
          <Button 
            onClick={saveSettings} 
            disabled={saving}
            className="bg-slate-800 hover:bg-slate-900"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>

        {/* IP Whitelist Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Admin Login IP Whitelist
                </CardTitle>
                <CardDescription>
                  Restrict admin portal access to specific IP addresses
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="whitelist-enabled" className="text-sm">
                  {whitelistEnabled ? 'Enabled' : 'Disabled'}
                </Label>
                <Switch
                  id="whitelist-enabled"
                  checked={whitelistEnabled}
                  onCheckedChange={setWhitelistEnabled}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Warning Banner */}
            {whitelistEnabled && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    IP Whitelist is Active
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Only users from whitelisted IP addresses can access the admin login page.
                    Make sure your IP is whitelisted before saving!
                  </p>
                </div>
              </div>
            )}

            {/* Allow Localhost Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Allow Localhost Access</Label>
                <p className="text-sm text-gray-500">
                  Allow access from localhost (127.0.0.1) for development
                </p>
              </div>
              <Switch
                checked={allowLocalhost}
                onCheckedChange={setAllowLocalhost}
                disabled={!whitelistEnabled}
              />
            </div>

            {/* Add New IP */}
            <div className="space-y-3">
              <Label className="font-medium">Add IP Address</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="192.168.1.1 or 10.0.0.0/24"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  className="w-48"
                />
                <Input
                  placeholder="Description (e.g., Office WiFi)"
                  value={newIpDescription}
                  onChange={(e) => setNewIpDescription(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={addIpAddress}
                  variant="outline"
                  disabled={!whitelistEnabled}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Supports IPv4 addresses (e.g., 192.168.1.1) and CIDR ranges (e.g., 192.168.1.0/24)
              </p>
            </div>

            {/* IP Address List */}
            <div className="space-y-3">
              <Label className="font-medium">Whitelisted IP Addresses</Label>
              
              {ipAddresses.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed">
                  <Info className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">No IP addresses whitelisted yet</p>
                  <p className="text-sm text-gray-400">
                    Add IP addresses above to restrict admin access
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ipAddresses.map((entry, index) => (
                    <div 
                      key={entry.id || entry.name || `${entry.title || "entry"}-${index}`}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <div>
                          <code className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {entry.ip_address}
                          </code>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {entry.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {entry.added_by && (
                          <Badge variant="outline" className="text-xs">
                            Added by {entry.added_by}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeIpAddress(entry.ip_address)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">How IP Whitelist Works:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>When enabled, only users from whitelisted IPs can access <code className="bg-blue-100 px-1 rounded">/admin/login</code></li>
                  <li>Users from non-whitelisted IPs will see an access denied message</li>
                  <li>Vendor login at <code className="bg-blue-100 px-1 rounded">/login</code> is not affected</li>
                  <li>CIDR notation allows whitelisting entire IP ranges</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* iter-231B.UI — Compliance Enforcement (Tier-2 GST) */}
        <Card data-testid="compliance-enforcement-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Compliance Enforcement (Tier-2 GST)
            </CardTitle>
            <CardDescription>
              Server-side guard for Shopify orders that violate a vendor&apos;s Tier-2
              GST composition-scheme home-state restriction. When auto-cancel is
              ON, violating orders are cancelled and refunded automatically.
              When OFF (log-only), violations are still recorded in{' '}
              <code className="bg-gray-100 px-1 rounded">compliance_violations</code>
              {' '}but no order is touched — useful while you watch the first
              few alerts and verify the data looks right.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {complianceLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <Label htmlFor="auto-cancel-tier2-toggle" className="font-medium">
                      Auto-cancel Tier-2 GST violations
                    </Label>
                    <p className="text-xs text-gray-500">
                      {autoCancelTier2
                        ? 'ENFORCING — violating orders are auto-cancelled + refunded via Shopify.'
                        : 'LOG-ONLY — violations are recorded but the order is not cancelled.'}
                    </p>
                    {complianceUpdatedAt && (
                      <p className="text-xs text-gray-400">
                        Last changed {new Date(complianceUpdatedAt).toLocaleString()}{' '}
                        {complianceUpdatedBy ? `by ${complianceUpdatedBy}` : ''}
                      </p>
                    )}
                  </div>
                  <Switch
                    id="auto-cancel-tier2-toggle"
                    data-testid="auto-cancel-tier2-toggle"
                    checked={autoCancelTier2}
                    disabled={complianceSaving}
                    onCheckedChange={saveComplianceSettings}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-gray-500">Total violations</p>
                    <p className="text-2xl font-semibold" data-testid="violation-count-total">
                      {violationCounts.total}
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg bg-red-50">
                    <p className="text-xs text-gray-500">Auto-cancelled</p>
                    <p className="text-2xl font-semibold text-red-700" data-testid="violation-count-cancelled">
                      {violationCounts.auto_cancelled}
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg bg-yellow-50">
                    <p className="text-xs text-gray-500">Logged-only</p>
                    <p className="text-2xl font-semibold text-yellow-700" data-testid="violation-count-logged">
                      {violationCounts.logged_only}
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg bg-orange-50">
                    <p className="text-xs text-gray-500">Cancel failed</p>
                    <p className="text-2xl font-semibold text-orange-700" data-testid="violation-count-failed">
                      {violationCounts.cancel_failed}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Recommended rollout
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-orange-900">
                    <li>Toggle OFF for the first 24 hours and watch the violation feed</li>
                    <li>Verify <code className="bg-orange-100 px-1 rounded">compliance_violations</code> records look sane</li>
                    <li>Flip ON once you are comfortable that the engine is identifying real GST breaches</li>
                  </ol>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminSecuritySettings;
